
import { useState, useEffect, useRef } from 'react';
import { useAbielAudio } from '@/components/audio/AbielAudioContext';

export interface ControlState {
  scrollY: number;
  isScrollingUp: boolean;
  isScrollingDown: boolean;
  lastScrollY: number;
  showControls: boolean;
  controlOpacity: number;
}

export function useControlStateManager() {
  const { isPlaying } = useAbielAudio();
  const lastScrollYRef = useRef(0);
  const [controlState, setControlState] = useState<ControlState>({
    scrollY: 0,
    isScrollingUp: false,
    isScrollingDown: false,
    lastScrollY: 0,
    showControls: true,
    controlOpacity: 1
  });

  useEffect(() => {
    let ticking = false;

    const updateScrollState = () => {
      const scrollY = window.scrollY;
      const isScrollingUp = scrollY < lastScrollYRef.current;
      const isScrollingDown = scrollY > lastScrollYRef.current;
      
      // Show controls when scrolling up or near top
      const showControls = isScrollingUp || scrollY < 100;
      
      // Calculate opacity based on scroll direction and audio state
      let controlOpacity = 1;
      if (isScrollingDown && scrollY > 200) {
        controlOpacity = 0.7;
      }
      if (isPlaying) {
        controlOpacity = Math.max(controlOpacity, 0.9);
      }

      setControlState({
        scrollY,
        isScrollingUp,
        isScrollingDown,
        lastScrollY: scrollY,
        showControls,
        controlOpacity
      });
      
      // Update the ref for next comparison
      lastScrollYRef.current = scrollY;
      ticking = false;
    };

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(updateScrollState);
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isPlaying]); // Removed controlState.lastScrollY from dependencies

  return controlState;
}
